import { ServiceRequestPayload } from '../types/ServiceRequest';

// Define a loose interface for the Wizard State to allow mapping
// In a real app, this would be the actual state interface from the context.
export interface WizardState {
    boat: {
        brand?: string;
        brandName?: string;
        model?: string;
        modelName?: string;
        registration?: string;
    };
    engine: {
        brand?: string;
        brandName?: string;
        model?: string;
        modelName?: string;
        year?: string;
        horsepower?: string;
        serialNumber?: string;
    };
    service: {
        selectedPackageId?: string; // ID string or 'other'
        packageTitle?: string;
        packagePrice?: string;
    };
    timeAndDelivery: {
        timePreference?: string; // '7days', '30days', 'specific'
        specificWeek?: string;
        deliveryMethod?: string; // 'water', 'trailer', 'pickup'
        pickupAddress?: {
            street?: string;
            zip?: string;
            city?: string;
            notes?: string;
        };
    };
    contact: {
        firstName?: string;
        lastName?: string;
        email?: string;
        phone?: string;
        notes?: string;
    };
}

export const buildPayload = (state: WizardState): ServiceRequestPayload => {
    // Requirements: if fallback ('other'), service_package = 0.
    // Otherwise use the selected ID.
    const serviceString = state.service.selectedPackageId || '';
    let packageId = 0;

    if (serviceString === 'other') {
        packageId = 0;
    } else {
        // Try parsing assuming it's a numeric ID string
        const parsed = parseInt(serviceString, 10);
        if (!isNaN(parsed)) {
            packageId = parsed;
        } else {
            // Fallback for legacy "basic/wide" strings if any
            const serviceMap: Record<string, number> = {
                'basic': 1,
                'wide': 2,
                'premium': 3,
            };
            packageId = serviceMap[serviceString] || 0;
        }
    }

    const isFallback = packageId === 0;

    // Helper to parse numbers safely
    const parseNum = (val?: string) => {
        const num = parseInt(val || '0', 10);
        return isNaN(num) ? 0 : num;
    };

    // Helper for string defaults
    const str = (val?: string) => val || "";

    // Combine time preference if specific
    let timeWindow = str(state.timeAndDelivery.timePreference);
    if (timeWindow === 'specific') {
        timeWindow = `Viikko ${str(state.timeAndDelivery.specificWeek)}`;
    }

    // Combine notes
    const noteParts: string[] = [];

    // 1. Pickup notes
    if (state.timeAndDelivery.deliveryMethod === 'pickup') {
        const addr = state.timeAndDelivery.pickupAddress;
        noteParts.push(`Nouto: ${str(addr?.street)}, ${str(addr?.zip)} ${str(addr?.city)}. Ohjeet: ${str(addr?.notes)}`);
    } else if (state.timeAndDelivery.deliveryMethod === 'water') {
        noteParts.push('Toimitus: Vesitse');
    } else if (state.timeAndDelivery.deliveryMethod === 'trailer') {
        noteParts.push('Toimitus: Trailerilla');
    }

    // 2. Contact notes
    if (state.contact.notes) {
        noteParts.push(`Lisätiedot: ${state.contact.notes}`);
    }

    return {
        // Map ID fields if they are numeric strings, otherwise map directly
        boat_hull_brand: str(state.boat.brand),
        boat_model: str(state.boat.model),
        boat_registration_number: str(state.boat.registration),

        engine_brand: str(state.engine.brand),
        engine_model: str(state.engine.model),
        engine_year: parseNum(state.engine.year),
        engine_horsepower: parseNum(state.engine.horsepower),
        engine_serial_number: str(state.engine.serialNumber),

        preferred_time_window: timeWindow,
        delivery_method: str(state.timeAndDelivery.deliveryMethod),

        customer_first_name: str(state.contact.firstName),
        customer_last_name: str(state.contact.lastName),
        customer_email: str(state.contact.email),
        customer_phone: str(state.contact.phone),

        customer_notes: noteParts.join('\n\n'),

        service_package: packageId,
        is_fallback: isFallback
    };
};
